import { Banner, Button, Modal, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AlarmClock, Eye, Workflow } from '@servicecompressionllc/sc-design-system/icons';
import { formatDay } from '../../../lib/format.js';
import { schedulePhrase, summaryText } from '../../workflowBuilder/summarize.js';
import { useWhatIWatchLogic, type Duty, type PanelWatch } from './useWhatIWatchLogic.js';

interface WhatIWatchPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  botId: string;
  botName: string;
}

const lowerFirst = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

/** "Checks every day at 7:00 AM until Nov 20", "When Unit alarm arrives", "Once on Oct 23 at 9:00 AM". */
export const whenLine = (watch: PanelWatch): string => {
  const runs = watch.schedule
    ? summaryText(schedulePhrase(watch.schedule))
    : `When ${watch.event?.title ?? 'its event'} arrives`;
  if (watch.kind === 'reminder') return runs;
  const once = watch.schedule !== null && 'on' in watch.schedule;
  // endsAt is the moment it stops, often the midnight after its last day.
  const until =
    watch.endsAt && !once
      ? ` until ${formatDay(new Date(new Date(watch.endsAt).getTime() - 1).toISOString())}`
      : '';
  return watch.schedule ? `Checks ${lowerFirst(runs)}${until}` : `${runs}${until}`;
};

const becauseLine = (duty: Duty): string =>
  duty.because.kind === 'group'
    ? `Because you’re in ${duty.because.groupName}`
    : 'When you’re named in the event';

const SectionLabel = ({ children }: { children: string }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-subtle">{children}</h3>
);

/** A bot's What I watch sheet: what they asked it to watch, and what's part of their job. */
export const WhatIWatchPanel = ({ open, onOpenChange, botId, botName }: WhatIWatchPanelProps) => {
  const logic = useWhatIWatchLogic(botId, open);

  return (
    <Modal side="right" open={open} onOpenChange={onOpenChange}>
      <Modal.Header>
        <div className="flex flex-col gap-1">
          <Modal.Title>What I watch</Modal.Title>
          <Modal.Description>
            {botName} tells you when one of these happens, and stays quiet about everything else.
          </Modal.Description>
        </div>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-3">
        {logic.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : logic.loadError ? (
          <Banner variant="danger">
            <Banner.Body>
              <Banner.Title>Couldn’t load what {botName} watches</Banner.Title>
              {logic.loadError.message}
            </Banner.Body>
          </Banner>
        ) : (
          <>
            <SectionLabel>You asked me to</SectionLabel>
            {logic.yours.length === 0 ? (
              <p className="text-sm text-ink-muted">
                Nothing yet. Ask {botName} to keep an eye on something or remind you of something.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {logic.yours.map((watch) => (
                  <li
                    key={watch.watchId}
                    className="flex items-start gap-2.5 rounded-lg border border-outline bg-surface p-3"
                  >
                    {watch.kind === 'reminder' ? (
                      <AlarmClock aria-hidden className="mt-0.5 size-4 flex-none text-ink-muted" />
                    ) : (
                      <Eye aria-hidden className="mt-0.5 size-4 flex-none text-ink-muted" />
                    )}
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="text-sm font-semibold text-ink">{watch.what}</span>
                      <span className="text-xs text-ink-muted">{whenLine(watch)}</span>
                      {watch.status === 'broken' ? (
                        <span className="text-xs text-ink-muted">
                          Not working right now
                          {watch.brokenReason ? `: ${watch.brokenReason.replace(/\.$/, '')}` : ''}.
                          Ask {botName} to fix it.
                        </span>
                      ) : watch.status === 'paused' ? (
                        <span className="text-xs text-ink-muted">Paused</span>
                      ) : null}
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={logic.stopping === watch.watchId}
                      onClick={() => logic.stop(watch.watchId)}
                    >
                      Stop
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {logic.stopError && (
              <p role="alert" className="text-sm text-danger">
                {logic.stopError.message}
              </p>
            )}
            <p className="text-xs text-ink-subtle">
              {logic.budgetPercentUsed !== null &&
                `Today your watches have used ${String(logic.budgetPercentUsed)}% of their daily budget. `}
              To add or change one, just ask {botName}.
            </p>

            <hr className="my-2 border-outline" />

            <SectionLabel>Part of your job</SectionLabel>
            {logic.partOfYourJob.length === 0 ? (
              <p className="text-sm text-ink-muted">
                Nothing your admins set up tells {botName} about you.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {logic.partOfYourJob.map((duty) => (
                  <li
                    key={duty.workflowId}
                    className="flex items-start gap-2.5 rounded-lg border border-outline bg-surface p-3"
                  >
                    <Workflow aria-hidden className="mt-0.5 size-4 flex-none text-ink-muted" />
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-sm font-semibold text-ink">{duty.name}</span>
                      <span className="text-xs text-ink-muted">{becauseLine(duty)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-ink-subtle">
              Your admins set these up. To stop hearing from {botName}, turn it off at the top of
              the conversation.
            </p>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};
